import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
    AlertTriangle,
    CheckCircle,
    Search,
    PenTool,
    ExternalLink,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Globe,
    Clock,
    Eye,
    Activity,
    Download,
    CheckSquare,
    Square,
    Building,
    CircleDot,
    Timer,
    FileText,
    History,
    Tag,
    UserCircle,
    Layers
} from 'lucide-react';
import './ResultsPage.css';
import { useUrlProcessResults, useJobFilterOptions } from '../hooks/useApi';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { jobsApi, violationsApi, violationApi, ViolationExportResponse, ViolationExportProgressResponse, ViolationExportDownloadResponse } from '../services/api';
import { decodeUrlForDisplay } from '../utils/urlDisplay';
import { buildViolationAnnotationTo } from '../utils/resultsReturnNavigation';
import { buildViolationExportFilterSummary } from '../utils/violationExportFilename';
import { useToast } from '../components/toast/ToastProvider';

const TIME_FILTER_MAP: Record<string, string> = {
    '今日': 'today',
    '今週': 'thisWeek',
    '今月': 'thisMonth',
};

const EXPORT_FORMAT = {
    PDF: 'pdf',
    EXCEL: 'excel',
    REVIEW_CAPTURE_EXCEL: 'review_capture_excel',
} as const;

type ExportFormat = typeof EXPORT_FORMAT[keyof typeof EXPORT_FORMAT];

/** Parse comma-separated query param (e.g. statuses=a,b). */
function parseListParamFromSearchParams(sp: URLSearchParams, key: string): string[] {
    const raw = sp.get(key);
    if (!raw) return [];
    return raw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
}

function parseJobIdsFromSearchParams(sp: URLSearchParams): string[] {
    const raw = sp.get('jobId') || sp.get('job_id');
    if (!raw) return [];
    return raw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
}

const ResultsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const toast = useToast();
    const violationReturnToPath = useMemo(
        () => `${location.pathname}${location.search}`,
        [location.pathname, location.search]
    );

    const [selectedStatuses, setSelectedStatuses] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'statuses'));
    const [selectedTags, setSelectedTags] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'tags'));
    const [selectedJobs, setSelectedJobs] = useState(() => parseJobIdsFromSearchParams(searchParams));
    const [selectedTime, setSelectedTime] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'time'));
    const [selectedClients, setSelectedClients] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'clients'));
    const [selectedAsps, setSelectedAsps] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'asps'));
    const [selectedMedia, setSelectedMedia] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'media'));
    const [selectedVersions, setSelectedVersions] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'versions'));
    const [selectedOperationCompanies, setSelectedOperationCompanies] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'operationCompanies'));
    const [selectedCreators, setSelectedCreators] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'creators'));
    const [selectedCategories, setSelectedCategories] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'categories'));
    const [categoryOptions, setCategoryOptions] = useState<Array<{ label: string; value: string }>>([]);
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
    const [currentPage, setCurrentPage] = useState(() => {
        const p = parseInt(searchParams.get('page') || '1', 10);
        return !Number.isNaN(p) && p > 0 ? p : 1;
    });
    const jobNameHydratedRef = useRef(false);
    /** Legacy `jobName` → id hydration updates `selectedJobs` after mount; do not treat as a user filter change. */
    const skipNextFilterPageResetRef = useRef(false);
    const [itemsPerPage] = useState(10);

    // Export functionality state
    const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [exportProgressDetails, setExportProgressDetails] = useState<{ completed: number; total: number }>({
        completed: 0,
        total: 0,
    });
    const [showExportModal, setShowExportModal] = useState(false);
    const [currentExportTaskId, setCurrentExportTaskId] = useState<string | null>(null);
    const [progressPollInterval, setProgressPollInterval] = useState<NodeJS.Timeout | null>(null);
    const [exportOptions, setExportOptions] = useState<{
        selectedUrls: string[];
        includeAll: boolean;
        filterByJob: string | undefined;
        filterByStatus: string | undefined;
        includeViolations: boolean;
        format: ExportFormat;
    }>({
        selectedUrls: [],
        includeAll: false,
        filterByJob: undefined,
        filterByStatus: undefined,
        includeViolations: true,
        format: EXPORT_FORMAT.EXCEL
    });

    const getUserFacingUrlErrorMessage = useCallback((urlResult: any): string | null => {
        const t = urlResult?.error_type || urlResult?.errorType;
        const msg = urlResult?.error_message_user || urlResult?.errorMessageUser;
        if (typeof msg === 'string' && msg.trim() !== '') return msg;
        if (t === 'not_found') return 'ページが存在しません';
        if (t === 'forbidden') return 'サイトへのアクセスがブロックされています';
        if (t === 'internal') return 'システムでエラーが発生しました';
        // Fallback: infer from status_code if present
        const sc = urlResult?.status_code ?? urlResult?.statusCode;
        if (sc === 404) return 'ページが存在しません';
        if (sc === 403) return 'サイトへのアクセスがブロックされています';
        // Old rows may not have error_type/status_code/error_message_user; keep blank as requested.
        return null;
    }, []);

    // Guideline options are provided by /jobs/filter-options; no need to call /guidelines here.

    // Support multi-select: if 1 selected -> use server-side filter; if >1 -> fetch all then client-filter
    const selectedJobIds: string[] = useMemo(() => selectedJobs, [selectedJobs]);

    const jobIdsForApi: string[] | undefined = selectedJobIds.length > 0 ? selectedJobIds : undefined;

    const jobIdsForClientFilter = useMemo(
        () => (selectedJobIds.length > 0 ? selectedJobIds : parseJobIdsFromSearchParams(searchParams)),
        [selectedJobIds, searchParams]
    );

    // Fetch URL process results with server-side job filter to avoid race/mapping issues
    // Map UI statuses (JP) to backend statuses
    const statusMap: Record<string, string> = {
        '保留中': 'pending',
        '処理中': 'processing',
        'レビュー待ち': 'review_pending',
        'レビュー済み': 'reviewed',
        '失敗': 'failed',
        '完了': 'completed',
    };
    const selectedBackendStatuses = selectedStatuses.map(s => statusMap[s]).filter(Boolean);

    const timeFiltersForApi = useMemo(() => selectedTime
        .map(filter => TIME_FILTER_MAP[filter])
        .filter((value): value is string => Boolean(value)), [selectedTime]);

    const selectedVersionNumbers = selectedVersions
        .map((value) => {
            const parsed = parseInt(value, 10);
            return Number.isNaN(parsed) ? null : parsed;
        })
        .filter((value): value is number => value !== null);

    const { data: urlProcessResultsData, loading: urlProcessResultsLoading, error: urlProcessResultsError, refetch: refetchUrlProcessResults } = useUrlProcessResults(
        currentPage,
        itemsPerPage,
        undefined,
        jobIdsForApi,
        selectedBackendStatuses,
        searchTerm,
        timeFiltersForApi,
        selectedClients,
        selectedVersionNumbers,
        selectedAsps,
        selectedMedia,
        selectedOperationCompanies,
        selectedCreators,
        selectedCategories.length > 0 ? selectedCategories : undefined,
    );
    const { data: filterOptionsData, loading: filterOptionsLoading, error: filterOptionsError } = useJobFilterOptions();

    // Extract filter options from API response (now includes jobs + categories)
    const filterOptions = filterOptionsData && typeof filterOptionsData === 'object' && 'data' in filterOptionsData
        ? (filterOptionsData as any).data
        : { guidelines: [], versions: [], asps: [], media_names: [], operation_companies: [], creators: [], jobs: [], categories: [] };

    const guidelineIdToNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        const guidelines = Array.isArray(filterOptions.guidelines) ? filterOptions.guidelines : [];
        guidelines.forEach((g: any) => {
            if (g?.id) map[g.id] = g.name || g.id;
        });
        return map;
    }, [filterOptions.guidelines]);

    const jobsList = Array.isArray(filterOptions.jobs) ? filterOptions.jobs : [];

    const jobIdToNameMap = useMemo(() => (
        jobsList.reduce((map: any, job: any) => {
            const jobName = job.job_name || job.jobName || 'Unnamed Job';
            map[job.id] = jobName;
            return map;
        }, {})
    ), [jobsList]);

    const jobNameToIdMap = useMemo(() => (
        jobsList.reduce((map: any, job: any) => {
            const jobName = job.job_name || job.jobName || 'Unnamed Job';
            if (!map[jobName]) map[jobName] = job.id;
            return map;
        }, {})
    ), [jobsList]);

    // Keep category dropdown options in sync with backend-provided category options
    useEffect(() => {
        const categories = Array.isArray(filterOptions.categories) ? filterOptions.categories : [];
        setCategoryOptions(categories.map((cat: any) => ({
            label: cat.title || cat.violation_category || cat.id,
            value: cat.id,
        })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filterOptions.categories ?? [])]);


    // Available statuses
    const availableStatuses = ['保留中', '処理中', 'レビュー待ち', 'レビュー済み', '失敗'];

    // Available tags
    const availableTags = ['緊急', '要確認', '承認済み', '却下', 'フォローアップ', '高優先度'];

    // Available time options
    const availableTimeOptions = ['今日', '今週', '今月'];

    const availableJobs = jobsList.map((job: any) => ({ label: job.job_name || job.jobName || 'Unnamed Job', value: job.id }));

    // Legacy `?jobName=` links: resolve to job id once filter-options jobs are loaded (same as previous init effect).
    useEffect(() => {
        if (jobNameHydratedRef.current) return;
        const name = searchParams.get('jobName')?.trim();
        if (!name) {
            jobNameHydratedRef.current = true;
            return;
        }
        if (Object.keys(jobNameToIdMap).length === 0 && filterOptionsLoading) return;

        const id = jobNameToIdMap[name];
        if (id) {
            skipNextFilterPageResetRef.current = true;
            setSelectedJobs((prev) => (prev.length === 0 ? [id] : prev));
        }
        jobNameHydratedRef.current = true;
    }, [searchParams, jobNameToIdMap, filterOptionsLoading]);

    // Keep URL query params in sync when filters change (functional updater avoids stale searchParams).
    useEffect(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);

            if (selectedStatuses.length > 0) {
                params.set('statuses', selectedStatuses.join(','));
            } else {
                params.delete('statuses');
            }

            if (selectedTags.length > 0) {
                params.set('tags', selectedTags.join(','));
            } else {
                params.delete('tags');
            }

            if (selectedJobs.length === 1) {
                params.set('jobId', selectedJobs[0]);
                params.delete('job_id');
                params.delete('jobName');
            } else if (selectedJobs.length > 1) {
                params.set('jobId', selectedJobs.join(','));
                params.delete('job_id');
                params.delete('jobName');
            } else {
                params.delete('jobId');
                params.delete('job_id');
                params.delete('jobName');
            }

            if (selectedTime.length > 0) {
                params.set('time', selectedTime.join(','));
            } else {
                params.delete('time');
            }

            if (selectedClients.length > 0) {
                params.set('clients', selectedClients.join(','));
            } else {
                params.delete('clients');
            }

            if (selectedAsps.length > 0) {
                params.set('asps', selectedAsps.join(','));
            } else {
                params.delete('asps');
            }

            if (selectedMedia.length > 0) {
                params.set('media', selectedMedia.join(','));
            } else {
                params.delete('media');
            }

            if (selectedVersions.length > 0) {
                params.set('versions', selectedVersions.join(','));
            } else {
                params.delete('versions');
            }

            if (selectedOperationCompanies.length > 0) {
                params.set('operationCompanies', selectedOperationCompanies.join(','));
            } else {
                params.delete('operationCompanies');
            }

            if (selectedCreators.length > 0) {
                params.set('creators', selectedCreators.join(','));
            } else {
                params.delete('creators');
            }

            if (selectedCategories.length > 0) {
                params.set('categories', selectedCategories.join(','));
            } else {
                params.delete('categories');
            }

            if (searchTerm) {
                params.set('search', searchTerm);
            } else {
                params.delete('search');
            }

            params.set('page', String(Math.max(1, currentPage)));

            return params;
        }, { replace: true });
    }, [
        setSearchParams,
        selectedStatuses,
        selectedTags,
        selectedJobs,
        selectedTime,
        selectedClients,
        selectedAsps,
        selectedMedia,
        selectedVersions,
        selectedOperationCompanies,
        selectedCreators,
        selectedCategories,
        searchTerm,
        currentPage
    ]);

    const resultsFilterFingerprint = useMemo(
        () =>
            JSON.stringify({
                statuses: [...selectedStatuses].slice().sort(),
                tags: [...selectedTags].slice().sort(),
                jobs: [...selectedJobs].slice().sort(),
                time: [...selectedTime].slice().sort(),
                clients: [...selectedClients].slice().sort(),
                asps: [...selectedAsps].slice().sort(),
                versions: [...selectedVersions].slice().sort(),
                media: [...selectedMedia].slice().sort(),
                operationCompanies: [...selectedOperationCompanies].slice().sort(),
                creators: [...selectedCreators].slice().sort(),
                categories: [...selectedCategories].slice().sort(),
                search: searchTerm.trim(),
            }),
        [
            selectedStatuses,
            selectedTags,
            selectedJobs,
            selectedTime,
            selectedClients,
            selectedAsps,
            selectedVersions,
            selectedMedia,
            selectedOperationCompanies,
            selectedCreators,
            selectedCategories,
            searchTerm,
        ]
    );
    const prevResultsFilterFingerprintRef = useRef<string | null>(null);

    // Reset to first page when filters change (fingerprint survives Strict Mode duplicate effect runs).
    useEffect(() => {
        if (skipNextFilterPageResetRef.current) {
            skipNextFilterPageResetRef.current = false;
            prevResultsFilterFingerprintRef.current = resultsFilterFingerprint;
            return;
        }
        if (prevResultsFilterFingerprintRef.current === null) {
            prevResultsFilterFingerprintRef.current = resultsFilterFingerprint;
            return;
        }
        if (prevResultsFilterFingerprintRef.current === resultsFilterFingerprint) {
            return;
        }
        prevResultsFilterFingerprintRef.current = resultsFilterFingerprint;
        setCurrentPage(1);
    }, [resultsFilterFingerprint]);

    const guidelineFilterOptions = useMemo(
        () => {
            const guidelines = Array.isArray(filterOptions.guidelines) ? filterOptions.guidelines : [];
            return guidelines.map((g: any) => ({
                label: g.name || g.id,
                value: g.id,
            }));
        },
        [filterOptions.guidelines]
    );

    const creatorOptions = (filterOptions.creators || []).map((creator: any) => ({
        label: creator.display_name || creator.name || creator.username || creator.email || creator.id,
        value: creator.id
    }));

    const creatorIdToLabel = useMemo(() => {
        const map: Record<string, string> = {};
        creatorOptions.forEach((option: { label: string; value: string }) => {
            map[option.value] = option.label;
        });
        return map;
    }, [creatorOptions]);

    const categoryIdToLabel = useMemo(() => {
        const map: Record<string, string> = {};
        categoryOptions.forEach((option) => {
            map[option.value] = option.label;
        });
        return map;
    }, [categoryOptions]);

    /** Same order as the filter bar on this page (top-to-bottom, left-to-right). */
    const exportFilterDisplayItems = useMemo(() => {
        const items: Array<{ key: string; label: string; value: string }> = [];
        const push = (key: string, label: string, values: string[]) => {
            if (values.length > 0) {
                items.push({ key, label, value: values.join(', ') });
            }
        };

        const search = searchTerm.trim();
        if (search) {
            items.push({ key: 'search', label: '検索', value: search });
        }
        push(
            'guidelines',
            'ガイドラインフィルター',
            selectedClients.map((id) => guidelineIdToNameMap[id] || id)
        );
        push(
            'versions',
            'バージョンフィルター',
            selectedVersions.map((version) => `v${version}`)
        );
        push('asps', 'ASPフィルター', selectedAsps);
        push('operationCompanies', '運営会社フィルター', selectedOperationCompanies);
        push('media', '媒体名フィルター', selectedMedia);
        push(
            'creators',
            '作成者フィルター',
            selectedCreators.map((id) => creatorIdToLabel[id] || id)
        );
        push(
            'jobs',
            'ジョブフィルター',
            selectedJobs.map((id) => jobIdToNameMap[id] || id)
        );
        push('statuses', 'ステータスフィルター', selectedStatuses);
        push('tags', 'タグフィルター', selectedTags);
        push('time', '時間フィルター', selectedTime);
        push(
            'categories',
            'カテゴリフィルター',
            selectedCategories.map((id) => categoryIdToLabel[id] || id)
        );

        return items;
    }, [
        searchTerm,
        selectedClients,
        guidelineIdToNameMap,
        selectedVersions,
        selectedAsps,
        selectedOperationCompanies,
        selectedMedia,
        selectedCreators,
        creatorIdToLabel,
        selectedJobs,
        jobIdToNameMap,
        selectedStatuses,
        selectedTags,
        selectedTime,
        selectedCategories,
        categoryIdToLabel,
    ]);

    // Export functionality
    const handleSelectAllUrls = () => {
        if (selectedUrls.length === urlProcessResults.length) {
            setSelectedUrls([]);
        } else {
            setSelectedUrls(urlProcessResults.map((url: any) => url.id));
        }
    };

    const handleSelectUrl = (urlId: string) => {
        setSelectedUrls(prev => {
            if (prev.includes(urlId)) {
                return prev.filter(id => id !== urlId);
            } else {
                return [...prev, urlId];
            }
        });
    };

    const markUrlReviewed = useCallback(async (result: any) => {
        const jobId = result?.job_id || result?.jobId || result?.jobID;
        const urlId = result?.id;
        const status = String(result?.status || '');
        if (!jobId || !urlId) return;
        if (!(status === 'review_pending' || status === 'completed')) return;

        await violationApi.markReviewed(String(jobId), String(urlId));
    }, []);

    const handleMarkSelectedReviewed = useCallback(async (results: any[]) => {
        if (selectedUrls.length === 0) return;

        const selected = (results || []).filter((r: any) => selectedUrls.includes(r.id));
        const eligible = selected.filter((r: any) => {
            const status = String(r?.status || '');
            return status === 'review_pending' || status === 'completed';
        });

        if (eligible.length === 0) {
            toast.warning('レビュー待ちのURLが選択されていません。', { title: '操作できません' });
            return;
        }

        try {
            // Mark reviewed sequentially to avoid overwhelming backend/optimistic lock retries.
            for (const r of eligible) {
                // eslint-disable-next-line no-await-in-loop
                await markUrlReviewed(r);
            }
            setSelectedUrls((prev) => prev.filter((id) => !eligible.some((r: any) => r.id === id)));
            refetchUrlProcessResults();
        } catch (e) {
            console.error('Failed to mark reviewed:', e);
            toast.error('レビュー済みへの更新に失敗しました。', { title: '更新失敗' });
        }
    }, [markUrlReviewed, refetchUrlProcessResults, selectedUrls, toast]);

    const handleExportClick = () => {
        setExportOptions({
            selectedUrls: selectedUrls,
            includeAll: true, // Default to "すべてのURL" (All URLs)
            filterByJob: selectedJobs.length > 0 ? selectedJobs[0] : undefined,
            filterByStatus: selectedStatuses.length > 0 ? (selectedStatuses[0] as 'review_pending' | 'reviewed') : undefined,
            includeViolations: true, // Default to include violations
            format: EXPORT_FORMAT.EXCEL
        });
        setShowExportModal(true);
    };

    const handleExportConfirm = async () => {
        setIsExporting(true);
        setExportProgress(0);
        setExportProgressDetails({ completed: 0, total: 0 });

        try {
            const allResults = await fetchAllUrlProcessResultsForExport();
            const filteredResults = filterUrlResults(allResults);

            const scopedResults = exportOptions.includeAll
                ? filteredResults
                : filteredResults.filter((result: any) => selectedUrls.includes(result.id));

            if (scopedResults.length === 0) {
                setIsExporting(false);
                setExportProgress(0);
                toast.info('エクスポート対象のURLがありません。', { title: 'エクスポート' });
                return;
            }

            const uniqueUrlsMap = new Map<string, { jobId: string; urlId: string }>();
            scopedResults.forEach((result: any) => {
                const jobId = result.job_id || result.jobId || result.jobID;
                const urlId = result.id;
                if (jobId && urlId) {
                    uniqueUrlsMap.set(`${jobId}:${urlId}`, { jobId, urlId });
                }
            });

            const urlsPayload = Array.from(uniqueUrlsMap.values());

            if (urlsPayload.length === 0) {
                setIsExporting(false);
                setExportProgress(0);
                toast.info('エクスポート対象のURLがありません。', { title: 'エクスポート' });
                return;
            }

            setExportProgressDetails({ completed: 0, total: urlsPayload.length });

            const filterSummary = buildViolationExportFilterSummary({
                selectedJobs,
                jobIdToNameMap,
                selectedStatuses,
                selectedTime,
                selectedClients,
                guidelineIdToNameMap,
                selectedVersions,
                selectedAsps,
                selectedMedia,
                selectedOperationCompanies,
                selectedCreators,
                creatorIdToLabel,
                selectedTags,
                selectedCategories,
                categoryOptions,
                searchTerm,
            });

            const exportRequest = {
                urls: urlsPayload,
                options: {
                    includeViolations: exportOptions.includeViolations,
                format: exportOptions.format || EXPORT_FORMAT.EXCEL,
                    ...(filterSummary ? { filterSummary } : {}),
                }
            };

            // Start batch export
            const result: ViolationExportResponse = await violationsApi.exportBatch(exportRequest.urls, exportRequest.options);
            const taskId = result.taskId;

            // Start progress tracking
            await trackExportProgress(taskId);

        } catch (error) {
            console.error('Export failed:', error);
            const formatName = exportOptions.format === EXPORT_FORMAT.EXCEL
                ? 'Excel'
                : exportOptions.format === EXPORT_FORMAT.REVIEW_CAPTURE_EXCEL
                    ? 'レビューキャプチャ付きExcel'
                    : 'PDF';
            toast.error(`${formatName}生成に失敗しました: ${(error as Error).message}`, { title: 'エクスポート失敗' });
            setIsExporting(false);
            setExportProgress(0);
            setExportProgressDetails({ completed: 0, total: 0 });
        }
    };

    const trackExportProgress = async (taskId: string) => {
        setCurrentExportTaskId(taskId);
        const pollInterval = setInterval(async () => {
            try {
                const progress: ViolationExportProgressResponse = await violationsApi.getExportProgress(taskId);

                // Store progress details
                setExportProgressDetails({
                    completed: progress.progress.completed || 0,
                    total: progress.progress.total || 0,
                });

                // Ensure percentage doesn't exceed 100%
                const percentage = Math.min(progress.progress.percentage, 100);
                setExportProgress(percentage);

                if (progress.status === 'completed') {
                    clearInterval(pollInterval);
                    setProgressPollInterval(null);
                    setCurrentExportTaskId(null);
                    await downloadExportFile(taskId);
                    setShowExportModal(false);
                    setSelectedUrls([]);
                    setIsExporting(false);
                    // Don't reset progress details immediately - let them show 4/4 until modal closes
                    setTimeout(() => {
                        setExportProgress(0);
                        setExportProgressDetails({ completed: 0, total: 0 });
                    }, 1000); // Reset after 1 second delay
                } else if (progress.status === 'failed' || progress.status === 'canceled') {
                    clearInterval(pollInterval);
                    setProgressPollInterval(null);
                    setCurrentExportTaskId(null);
                    if (progress.status === 'canceled') {
                        setShowExportModal(false);
                        setIsExporting(false);
                        setExportProgress(0);
                        setExportProgressDetails({ completed: 0, total: 0 });
                    } else {
                        throw new Error(progress.errorMessage || 'Export failed');
                    }
                }
            } catch (error) {
                clearInterval(pollInterval);
                setProgressPollInterval(null);
                setCurrentExportTaskId(null);
                console.error('Progress tracking failed:', error);
                // Don't show error if it's a 404 (job was canceled) or if status is canceled
                const errorStatus = (error as any)?.status;
                const errorMessage = (error as Error).message;
                if (errorStatus !== 404 && !errorMessage.includes('canceled')) {
                    toast.error(`エクスポートの進行状況の取得に失敗しました: ${errorMessage}`, { title: 'エクスポート失敗' });
                }
                setIsExporting(false);
                setExportProgress(0);
                setExportProgressDetails({ completed: 0, total: 0 });
            }
        }, 1000); // Poll every second
        setProgressPollInterval(pollInterval);
    };

    const downloadExportFile = async (taskId: string) => {
        try {
            const result: ViolationExportDownloadResponse = await violationsApi.getExportDownload(taskId);

            // Download the file with authentication
            const blob = await violationsApi.downloadExportFile(result.downloadUrl);

            // Create download link from blob
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = result.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            toast.error(`ファイルのダウンロードに失敗しました: ${(error as Error).message}`, { title: 'ダウンロード失敗' });
        }
    };

    const handleExportCancel = async () => {
        // If there's an active export, cancel it
        if (currentExportTaskId && isExporting) {
            try {
                await violationsApi.cancelExport(currentExportTaskId);
                console.log(`Cancelled export job ${currentExportTaskId}`);
            } catch (error) {
                console.error('Failed to cancel export:', error);
                // Continue with cleanup even if cancel request fails
            }
        }

        // Stop progress tracking
        if (progressPollInterval) {
            clearInterval(progressPollInterval);
            setProgressPollInterval(null);
        }

        setShowExportModal(false);
        setIsExporting(false);
        setExportProgress(0);
        setExportProgressDetails({ completed: 0, total: 0 });
        setCurrentExportTaskId(null);
    };

    // Handle click outside modal
    const handleModalOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            handleExportCancel();
        }
    };

    // Real-time updates for job progress (refresh data periodically)
    useEffect(() => {
        const interval = setInterval(() => {
            // Only refresh if we have a jobId and the job is still processing
            if (selectedJobs.length > 0) {
                // Note: Real-time updates would need to be implemented with the new API
                // For now, we'll rely on manual refresh
            }
        }, 3000); // Refresh every 3 seconds for more responsive updates

        return () => clearInterval(interval);
    }, [selectedJobs]);

    // Use the new API loading and error states
    const loading = urlProcessResultsLoading;
    const error = urlProcessResultsError;

    // Get pagination info from API response
    const totalPages = urlProcessResultsData && typeof urlProcessResultsData === 'object' && 'pagination' in urlProcessResultsData
        ? (urlProcessResultsData as any).pagination.total_pages
        : 1;

    // Calculate pagination display info
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;


    const getStatusColor = (status: string) => {
        switch (status) {
            case 'processing_pending':
                return '#6b7280';
            case 'processing':
                return '#3b82f6';
            case 'review_pending':
                return '#f59e0b';
            case 'reviewed':
                return '#10b981';
            case 'completed':
                return '#10b981';
            case 'failed':
                return '#ef4444';
            case 'pending':
                return '#6b7280';
            default:
                return '#6b7280';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
                return '保留中';
            case 'processing':
                return '処理中';
            case 'review_pending':
                return 'レビュー待ち';
            case 'reviewed':
                return 'レビュー済み';
            case 'failed':
                return '失敗';
            case 'processing_pending':
                return '保留中';
            case 'completed':
                return '完了'; // Legacy status, should be review_pending
            default:
                return status.toUpperCase();
        }
    };

    // Get URL process results data for display
    const allUrlProcessResults = urlProcessResultsData && typeof urlProcessResultsData === 'object' && 'data' in urlProcessResultsData
        ? (urlProcessResultsData as any).data
        : [];

    // Categories are provided by /jobs/filter-options; no need to fetch guideline versions per result.

    const filterUrlResults = useCallback((results: any[]) => {
        return results.filter((result: any) => {
            // Apply status filter
            let statusMatch = true;
            if (selectedStatuses.length > 0) {
                statusMatch = selectedStatuses.some(selectedStatus => {
                    switch (selectedStatus) {
                        case '保留中':
                            return result.status === 'pending';
                        case '処理中':
                            return result.status === 'processing';
                        case 'レビュー待ち':
                            return result.status === 'review_pending' || result.status === 'completed';
                        case 'レビュー済み':
                            return result.status === 'reviewed';
                        case '失敗':
                            return result.status === 'failed';
                        default:
                            return false;
                    }
                });
            }

            // Apply tag filter
            let tagMatch = true;
            if (selectedTags.length > 0) {
                tagMatch = selectedTags.some(selectedTag =>
                    result.tags && result.tags.includes(selectedTag)
                );
            }

            // Apply job filter by selected job IDs (or raw jobId from URL before state catches up)
            let jobMatch = true;
            if (jobIdsForClientFilter.length > 0) {
                jobMatch = jobIdsForClientFilter.includes(result.job_id);
            }

            // Apply time filter
            let timeMatch = true;
            if (selectedTime.length > 0) {
                const resultDate = new Date(result.processed_at || result.created_at);
                const now = new Date();
                timeMatch = selectedTime.some(timeFilter => {
                    switch (timeFilter) {
                        case '今日':
                            return resultDate.toDateString() === now.toDateString();
                        case '今週':
                            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                            return resultDate >= weekAgo;
                        case '今月':
                            return resultDate.getMonth() === now.getMonth() && resultDate.getFullYear() === now.getFullYear();
                        default:
                            return false;
                    }
                });
            }

            // Apply guideline filter
            let guidelineMatch = true;
            if (selectedClients.length > 0) {
                const guidelineId =
                    result.guideline_id ??
                    result.guidelineId ??
                    result.job?.guideline_id ??
                    result.job?.guidelineId ??
                    result.client ??
                    result.job?.client ??
                    '';
                guidelineMatch = guidelineId ? selectedClients.includes(guidelineId) : false;
            }

            // Apply ASP filter
            let aspMatch = true;
            if (selectedAsps.length > 0) {
                const aspName = result.asp || result.job?.asp || '';
                aspMatch = selectedAsps.includes(aspName);
            }

            // Apply version filter
            let versionMatch = true;
            if (selectedVersions.length > 0) {
                const versionValue = result.version || result.job?.version;
                versionMatch = versionValue !== undefined && selectedVersions.includes(String(versionValue));
            }

            // Apply media filter
            let mediaMatch = true;
            if (selectedMedia.length > 0) {
                const mediaName = result.media || result.job?.media || result.upload?.media || '';
                mediaMatch = selectedMedia.includes(mediaName);
            }

            // Apply operation company filter
            let operationCompanyMatch = true;
            if (selectedOperationCompanies.length > 0) {
                const companyName = result.operation_company || result.job?.operation_company || '';
                operationCompanyMatch = selectedOperationCompanies.includes(companyName);
            }

            // Apply creator filter
            let creatorMatch = true;
            if (selectedCreators.length > 0) {
                const creatorInfo = result.created_by || result.createdBy;
                const creatorId = creatorInfo?.id || creatorInfo?.username || creatorInfo?.email;
                creatorMatch = creatorId ? selectedCreators.includes(creatorId) : false;
            }

            // Apply search filter
            let searchMatch = true;
            if (searchTerm.trim()) {
                const searchLower = searchTerm.toLowerCase();
                const guidelineName = (result.guideline_name || result.guidelineName || '').toLowerCase();
                const rawUrl = (result.url || '').toLowerCase();
                const displayUrl = decodeUrlForDisplay(result.url || '').toLowerCase();
                searchMatch =
                    rawUrl.includes(searchLower) ||
                    displayUrl.includes(searchLower) ||
                    result.job_name?.toLowerCase().includes(searchLower) ||
                    result.job_id?.toLowerCase().includes(searchLower) ||
                    guidelineName.includes(searchLower);
            }

            // Category filter is now handled on the backend, no need for client-side filtering

            return statusMatch && tagMatch && jobMatch && timeMatch && guidelineMatch && aspMatch && versionMatch && mediaMatch && operationCompanyMatch && creatorMatch && searchMatch;
        });
    }, [selectedStatuses, selectedTags, jobIdsForClientFilter, selectedTime, selectedClients, selectedAsps, selectedVersions, selectedMedia, selectedOperationCompanies, selectedCreators, searchTerm]);

    // Apply status filter, tag filter, job filter and search filter using useMemo to prevent infinite re-renders
    const urlProcessResults = useMemo(() => filterUrlResults(allUrlProcessResults), [filterUrlResults, allUrlProcessResults]);

    const totalFilteredCount = useMemo(() => {
        if (urlProcessResultsData && typeof urlProcessResultsData === 'object' && 'pagination' in urlProcessResultsData) {
            const pagination = (urlProcessResultsData as any).pagination || {};
            return pagination.total_items ?? pagination.totalItems ?? urlProcessResults.length;
        }
        return urlProcessResults.length;
    }, [urlProcessResultsData, urlProcessResults.length]);

    const fetchAllUrlProcessResultsForExport = useCallback(async () => {
        const aggregatedResults: any[] = [];
        const pageSize = 200;
        let page = 1;
        let hasNext = true;
        let safetyCounter = 0;
        const maxIterations = 1000;

        const jobIdsParam = jobIdsForApi && jobIdsForApi.length > 0 ? jobIdsForApi : undefined;
        const statusParam = selectedBackendStatuses.length > 0 ? selectedBackendStatuses : undefined;
        const searchParam = searchTerm.trim() !== '' ? searchTerm.trim() : undefined;
        const timeFiltersParam = timeFiltersForApi.length > 0 ? timeFiltersForApi : undefined;
        const clientsParam = selectedClients.length > 0 ? selectedClients : undefined;
        const aspsParam = selectedAsps.length > 0 ? selectedAsps : undefined;
        const mediaParam = selectedMedia.length > 0 ? selectedMedia : undefined;
        const versionsParam = selectedVersionNumbers.length > 0 ? selectedVersionNumbers : undefined;
        const operationCompaniesParam = selectedOperationCompanies.length > 0 ? selectedOperationCompanies : undefined;
        const creatorsParam = selectedCreators.length > 0 ? selectedCreators : undefined;
        const categoriesParam = selectedCategories.length > 0 ? selectedCategories : undefined;

        while (hasNext && safetyCounter < maxIterations) {
            safetyCounter += 1;
            const response: any = await jobsApi.getUrlProcessResults(
                page,
                pageSize,
                undefined,
                jobIdsParam,
                statusParam,
                searchParam,
                timeFiltersParam,
                clientsParam,
                versionsParam,
                aspsParam,
                mediaParam,
                operationCompaniesParam,
                creatorsParam,
                categoriesParam,
            );

            const pageResults = response?.data || [];
            aggregatedResults.push(...pageResults);

            const pagination = response?.pagination || {};
            const currentPageValue = pagination.current_page ?? pagination.currentPage ?? page;
            const totalPages = pagination.total_pages ?? pagination.totalPages;
            const hasNextFlag = pagination.has_next ?? pagination.hasNext;

            if (typeof hasNextFlag === 'boolean') {
                hasNext = hasNextFlag;
            } else if (typeof totalPages === 'number') {
                hasNext = currentPageValue < totalPages;
            } else {
                hasNext = pageResults.length === pageSize;
            }

            if (!hasNext) {
                break;
            }

            page = currentPageValue + 1;
        }

        return aggregatedResults;
    }, [jobIdsForApi, selectedBackendStatuses, searchTerm, timeFiltersForApi, selectedClients, selectedAsps, selectedVersions, selectedMedia, selectedOperationCompanies, selectedCreators, selectedCategories]);

    // Derive violations on demand if ever needed to display

    // Calculate stats from /url-process-results response (backend now embeds stats to avoid extra request)
    const statsData = urlProcessResultsData && typeof urlProcessResultsData === 'object' && 'stats' in urlProcessResultsData
        ? (urlProcessResultsData as any).stats
        : null;

    const stats = statsData ? {
        totalUrls: statsData.total_urls || 0,
        processingPendingUrls: statsData.status_counts?.pending || 0,
        processingUrls: statsData.status_counts?.processing || 0,
        reviewPendingUrls: (statsData.status_counts?.review_pending || 0) + (statsData.status_counts?.completed || 0), // Include legacy completed as review_pending
        reviewedUrls: statsData.status_counts?.reviewed || 0,
        completedUrls: 0, // No longer used
        failedUrls: statsData.status_counts?.failed || 0,
        totalViolations: statsData.total_violations || 0,
        pendingViolations: statsData.violation_status_counts?.pending || 0,
        reviewedViolations: statsData.violation_status_counts?.reviewed || 0,
        approvedViolations: 0, // No longer used
        rejectedViolations: 0 // No longer used
    } : {
        totalUrls: 0,
        processingPendingUrls: 0,
        processingUrls: 0,
        reviewPendingUrls: 0,
        reviewedUrls: 0,
        completedUrls: 0,
        failedUrls: 0,
        totalViolations: 0,
        pendingViolations: 0,
        reviewedViolations: 0,
        approvedViolations: 0,
        rejectedViolations: 0
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">URL処理結果</h1>
                <p className="page-description">
                    処理されたURLの結果を確認・管理します。
                </p>
            </div>

            {/* Stats Overview */}
            <div className="stats-section">
                {/* Error Display */}
                {error && (
                    <div className="error-state">
                        <AlertTriangle size={20} />
                        <span>
                            {error}
                        </span>
                    </div>
                )}

                {/* Loading State for Stats */}
                {loading && (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>統計情報を読み込み中...</span>
                    </div>
                )}

                {!loading && (
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Globe size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.totalUrls}</div>
                                <div className="stat-label">総URL数</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon processing-pending">
                                <Clock size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.processingPendingUrls}</div>
                                <div className="stat-label">保留中</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon processing">
                                <Activity size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.processingUrls}</div>
                                <div className="stat-label">処理中</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon review-pending">
                                <Eye size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.reviewPendingUrls}</div>
                                <div className="stat-label">レビュー待ち</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon completed">
                                <CheckCircle size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.reviewedUrls}</div>
                                <div className="stat-label">レビュー済み</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon failed">
                                <AlertTriangle size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.failedUrls}</div>
                                <div className="stat-label">失敗</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="search-box">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="URLまたはジョブ名で検索..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="filter-controls">
                        <button
                            className="btn btn-primary export-btn"
                            onClick={handleExportClick}
                            disabled={isExporting || urlProcessResults.length === 0}
                        >
                            <Download size={16} />
                            {isExporting ? 'エクスポート中...' : 'エクスポート'}
                        </button>
                    </div>
                </div>

                {/* Additional Filters Row */}
                <div className="filters-row additional-filters">
                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FileText size={12} />
                                ガイドライン
                            </span>
                        }
                        options={guidelineFilterOptions}
                        selectedValues={selectedClients}
                        onSelectionChange={setSelectedClients}
                        placeholder="すべてのガイドライン"
                        searchPlaceholder="ガイドラインを検索..."
                        loading={filterOptionsLoading}
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <History size={12} />
                                バージョン
                            </span>
                        }
                        options={(filterOptions.versions || []).map((version: number) => version.toString())}
                        selectedValues={selectedVersions}
                        onSelectionChange={setSelectedVersions}
                        placeholder="すべてのバージョン"
                        searchPlaceholder="バージョンを検索..."
                        loading={filterOptionsLoading}
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Building size={12} />
                                ASP
                            </span>
                        }
                        options={filterOptions.asps || []}
                        selectedValues={selectedAsps}
                        onSelectionChange={setSelectedAsps}
                        placeholder="すべてのASP"
                        searchPlaceholder="ASPを検索..."
                        loading={filterOptionsLoading}
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Building size={12} />
                                運営会社
                            </span>
                        }
                        options={filterOptions.operation_companies || []}
                        selectedValues={selectedOperationCompanies}
                        onSelectionChange={setSelectedOperationCompanies}
                        placeholder="すべての運営会社"
                        searchPlaceholder="運営会社を検索..."
                        loading={filterOptionsLoading}
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Building size={12} />
                                媒体名
                            </span>
                        }
                        options={filterOptions.media_names || []}
                        selectedValues={selectedMedia}
                        onSelectionChange={setSelectedMedia}
                        placeholder="すべての媒体"
                        searchPlaceholder="媒体名を検索..."
                        loading={filterOptionsLoading}
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <UserCircle size={12} />
                                作成者
                            </span>
                        }
                        options={creatorOptions}
                        selectedValues={selectedCreators}
                        onSelectionChange={setSelectedCreators}
                        placeholder="すべての作成者"
                        searchPlaceholder="作成者を検索..."
                        loading={filterOptionsLoading}
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Activity size={12} />
                                ジョブ
                            </span>
                        }
                        options={availableJobs}
                        selectedValues={selectedJobs}
                        onSelectionChange={setSelectedJobs}
                        placeholder="すべてのジョブ"
                        searchPlaceholder="ジョブを検索..."
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <CircleDot size={12} />
                                ステータス
                            </span>
                        }
                        options={availableStatuses}
                        selectedValues={selectedStatuses}
                        onSelectionChange={setSelectedStatuses}
                        placeholder="すべてのステータス"
                        searchPlaceholder="ステータスを検索..."
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Tag size={12} />
                                タグ
                            </span>
                        }
                        options={availableTags}
                        selectedValues={selectedTags}
                        onSelectionChange={setSelectedTags}
                        placeholder="すべてのタグ"
                        searchPlaceholder="タグを検索..."
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Timer size={12} />
                                時間
                            </span>
                        }
                        options={availableTimeOptions}
                        selectedValues={selectedTime}
                        onSelectionChange={setSelectedTime}
                        placeholder="すべての時間"
                        searchPlaceholder="時間を検索..."
                    />

                    <MultiSelectFilter
                        label={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Layers size={12} />
                                カテゴリコンテンツ存在
                            </span>
                        }
                        options={categoryOptions}
                        selectedValues={selectedCategories}
                        onSelectionChange={setSelectedCategories}
                        placeholder="すべてのカテゴリ"
                        searchPlaceholder="カテゴリを検索..."
                        loading={categoryOptions.length === 0 && allUrlProcessResults.length > 0}
                    />
                </div>
            </div>

            {/* Content Section - Unified URL Process Results */}
            <div className="violations-section">
                {/* Loading State */}
                {loading && (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>URL一覧を読み込み中...</span>
                    </div>
                )}

                {/* URL Process Results List */}
                {!loading && urlProcessResults.length > 0 && (
                    <div className="job-urls-section">
                        {/* URL List */}
                        <div className="urls-list">
                            <div className="section-header">
                                <h3 className="section-title">URL一覧</h3>
                                <div className="selection-controls">
                                    <button
                                        className="select-all-btn"
                                        onClick={handleSelectAllUrls}
                                        disabled={urlProcessResults.length === 0}
                                    >
                                        {selectedUrls.length === urlProcessResults.length && urlProcessResults.length > 0 ? (
                                            <CheckSquare size={16} />
                                        ) : (
                                            <Square size={16} />
                                        )}
                                        {selectedUrls.length === urlProcessResults.length && urlProcessResults.length > 0 ? 'すべて解除' : 'すべて選択'}
                                    </button>
                                    <span className="selection-count">
                                        {selectedUrls.length} / {urlProcessResults.length} 選択中
                                    </span>
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => handleMarkSelectedReviewed(urlProcessResults)}
                                        disabled={selectedUrls.length === 0}
                                        title="選択したURLをレビュー済みにします"
                                    >
                                        レビュー済みにする
                                    </button>
                                </div>
                            </div>
                            <div className="url-list">
                                {urlProcessResults.map((urlResult: any, index: number) => {
                                    const urlId = urlResult.id;
                                    const urlDisplay = decodeUrlForDisplay(String(urlResult.url || ''));
                                    return (
                                        <div key={urlId} className={`violation-item status-${urlResult.status}`}>
                                            <div className="violation-header">
                                                <div className="url-selection">
                                                    <button
                                                        className="url-checkbox"
                                                        onClick={() => handleSelectUrl(urlId)}
                                                    >
                                                        {selectedUrls.includes(urlId) ? (
                                                            <CheckSquare size={16} />
                                                        ) : (
                                                            <Square size={16} />
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="violation-info">
                                                    <div className="violation-details">
                                                        <h3 className="violation-title">{urlDisplay}</h3>
                                                        <div className="violation-meta">
                                                        </div>
                                                        <div className="violation-client-info">
                                                            <span className="violation-client">
                                                                <FileText size={11} />
                                                                ガイドライン: {urlResult.guideline_name || urlResult.guideline_id || urlResult.client || 'N/A'}
                                                            </span>
                                                            <span className="violation-version">
                                                                <History size={11} />
                                                                バージョン: {urlResult.version ? `v${urlResult.version}` : 'N/A'}
                                                            </span>
                                                            <span className="violation-asp">
                                                                <Building size={11} />
                                                                ASP: {urlResult.asp || 'N/A'}
                                                            </span>
                                                            <span className="violation-operation-company">
                                                                <Building size={11} />
                                                                運営会社: {urlResult.operation_company || 'N/A'}
                                                            </span>
                                                            <span className="violation-media">
                                                                <Building size={11} />
                                                                媒体名: {urlResult.media || 'N/A'}
                                                            </span>
                                                            {urlResult.created_by && (
                                                                <span className="violation-creator">
                                                                    <UserCircle size={11} />
                                                                    作成者: {urlResult.created_by.display_name || urlResult.created_by.name || urlResult.created_by.username || urlResult.created_by.email || urlResult.created_by.id}
                                                                </span>
                                                            )}
                                                            <span className="job-name">
                                                                <Activity size={11} />
                                                                {urlResult.job_name || 'Unnamed Job'}
                                                            </span>
                                                        </div>
                                                        <p className="violation-description">
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="violation-actions">
                                                    {/* Show review button for review_pending jobs */}
                                                    {urlResult.status === 'review_pending' && (
                                                        <Link
                                                            to={buildViolationAnnotationTo(
                                                                urlResult.job_id,
                                                                urlId,
                                                                violationReturnToPath
                                                            )}
                                                            className="btn btn-primary btn-sm"
                                                        >
                                                            <PenTool size={16} />
                                                            レビュー
                                                        </Link>
                                                    )}

                                                    {(urlResult.status === 'review_pending' || urlResult.status === 'completed') && (
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={async () => {
                                                                try {
                                                                    await markUrlReviewed(urlResult);
                                                                    refetchUrlProcessResults();
                                                                } catch (e) {
                                                                    console.error('Failed to mark reviewed:', e);
                                                                    toast.error('レビュー済みへの更新に失敗しました。', { title: '更新失敗' });
                                                                }
                                                            }}
                                                        >
                                                            レビュー済み
                                                        </button>
                                                    )}

                                                    {/* Show display button for reviewed jobs */}
                                                    {urlResult.status === 'reviewed' && (
                                                        <Link
                                                            to={buildViolationAnnotationTo(
                                                                urlResult.job_id,
                                                                urlId,
                                                                violationReturnToPath
                                                            )}
                                                            className="btn btn-secondary btn-sm"
                                                        >
                                                            <PenTool size={16} />
                                                            表示
                                                        </Link>
                                                    )}

                                                    {/* Legacy support for completed status */}
                                                    {urlResult.status === 'completed' && (
                                                        <Link
                                                            to={buildViolationAnnotationTo(
                                                                urlResult.job_id,
                                                                urlId,
                                                                violationReturnToPath
                                                            )}
                                                            className="btn btn-primary btn-sm"
                                                        >
                                                            <PenTool size={16} />
                                                            レビュー
                                                        </Link>
                                                    )}

                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => window.open(urlDisplay, '_blank')}
                                                    >
                                                        <ExternalLink size={16} />
                                                        URLを開く
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="violation-footer">
                                                <div className="violation-meta-info">
                                                    {urlResult.is_no_ad_notation && (
                                                        <span className="no-ad-notation-badge">
                                                            広告記載なし
                                                        </span>
                                                    )}
                                                    <span className="violation-confidence">
                                                        {(urlResult.violation_count ?? urlResult.violationCount ?? urlResult.violations?.filter((v: any) => !v.deleted).length ?? 0)} 違反
                                                    </span>
                                                    <span className="violation-date">
                                                        <Calendar size={14} />
                                                        {new Date(urlResult.processed_at).toLocaleDateString()}
                                                    </span>
                                                    {urlResult.response_time && (
                                                        <span className="error-info">応答時間: {urlResult.response_time.toFixed(2)}秒</span>
                                                    )}
                                                </div>
                                                <div className="violation-tags">
                                                    <div className="violation-status">
                                                        <span className="status-badge" style={{ backgroundColor: getStatusColor(urlResult.status) }}>
                                                            {getStatusText(urlResult.status)}
                                                        </span>
                                                    </div>
                                                            {urlResult.status === 'failed' && (
                                                                <div className="tags-container">
                                                                    {(() => {
                                                                        const errorText = getUserFacingUrlErrorMessage(urlResult);
                                                                        if (!errorText) return null;
                                                                        return (
                                                                            <span className="url-error-pill" role="note" aria-label="URL error">
                                                                                <span className="url-error-pill__text">
                                                                                    {errorText}
                                                                                </span>
                                                                                {(urlResult.has_traceback || urlResult.hasTraceback) && (
                                                                                    <Link
                                                                                        to={`/url-error/${urlResult.job_id}/${urlId}`}
                                                                                        className="url-error-pill__link"
                                                                                    >
                                                                                        詳細
                                                                                    </Link>
                                                                                )}
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                    {urlResult.tags && urlResult.tags.length > 0 && (
                                                        <div className="tags-container">
                                                            {urlResult.tags.map((tag: string, tagIndex: number) => (
                                                                <span key={tagIndex} className={`tag-badge ${tag.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <div className="pagination-info">
                                    {urlProcessResults.length} URL中 {startIndex + 1}-{Math.min(endIndex, urlProcessResults.length)} を表示
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <ChevronLeft size={16} />
                                        前へ
                                    </button>

                                    <div className="pagination-numbers">
                                        {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                                            let pageNum: number;
                                            if (totalPages <= 10) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 4) {
                                                pageNum = totalPages - 9 + i;
                                            } else {
                                                pageNum = currentPage - 4 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        次へ
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Empty State */}
                {!loading && urlProcessResults.length === 0 && (
                    <div className="empty-state">
                        <Globe size={48} className="empty-icon" />
                        <h3>URLが見つかりません</h3>
                        <p>現在のフィルターに一致するURLがありません</p>
                    </div>
                )}
            </div>

            {/* Export Modal */}
            {
                showExportModal && (
                    <div className="modal-overlay" onClick={handleModalOverlayClick}>
                        <div className="modal-content export-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>エクスポート</h3>
                                <button className="modal-close" onClick={handleExportCancel}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="export-options">
                                    <h4>エクスポート設定</h4>

                                    <div className="export-option">
                                        <label>
                                            <input
                                                type="radio"
                                                name="exportScope"
                                                checked={!exportOptions.includeAll}
                                                onChange={() => setExportOptions(prev => ({ ...prev, includeAll: false }))}
                                            />
                                            選択されたURLのみ ({selectedUrls.length}件)
                                        </label>
                                    </div>

                                    <div className="export-option">
                                        <label>
                                            <input
                                                type="radio"
                                                name="exportScope"
                                                checked={exportOptions.includeAll}
                                                onChange={() => setExportOptions(prev => ({ ...prev, includeAll: true }))}
                                            />
                                            すべてのURL ({totalFilteredCount}件)
                                        </label>
                                    </div>

                                    <div className="export-format-section">
                                        <h5>エクスポート形式</h5>
                                        <div className="export-option">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="exportFormat"
                                                    value={EXPORT_FORMAT.EXCEL}
                                                    checked={exportOptions.format === EXPORT_FORMAT.EXCEL}
                                                    onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value as ExportFormat }))}
                                                />
                                                Excel形式（.xlsx）
                                            </label>
                                        </div>
                                        <div className="export-option">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="exportFormat"
                                                    value={EXPORT_FORMAT.PDF}
                                                    checked={exportOptions.format === EXPORT_FORMAT.PDF}
                                                    onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value as ExportFormat }))}
                                                />
                                                PDF形式（ZIPファイル）
                                            </label>
                                        </div>
                                        <div className="export-option">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="exportFormat"
                                                    value={EXPORT_FORMAT.REVIEW_CAPTURE_EXCEL}
                                                    checked={exportOptions.format === EXPORT_FORMAT.REVIEW_CAPTURE_EXCEL}
                                                    onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value as ExportFormat }))}
                                                />
                                                レビューキャプチャ付きExcel（.xlsx）
                                            </label>
                                        </div>
                                    </div>

                                    {exportOptions.format === EXPORT_FORMAT.PDF && (
                                        <div className="export-format-section">
                                            <h5>PDFオプション</h5>
                                            <div className="export-option">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={exportOptions.includeViolations}
                                                        onChange={(e) => setExportOptions(prev => ({ ...prev, includeViolations: e.target.checked }))}
                                                    />
                                                    違反データ付きPDF（検出された違反をハイライト表示）
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    {exportFilterDisplayItems.map((item) => (
                                        <div key={item.key} className="export-info">
                                            <strong>{item.label}:</strong> {item.value}
                                        </div>
                                    ))}

                                    <div className="export-info">
                                        <strong>エクスポート形式:</strong> {
                                            exportOptions.format === EXPORT_FORMAT.EXCEL
                                                ? 'Excelファイル（.xlsx）'
                                                : exportOptions.format === EXPORT_FORMAT.PDF
                                                    ? `ZIPファイル（個別PDF${exportOptions.includeViolations ? '、違反データ付き' : ''}）`
                                                    : 'レビューキャプチャ付きExcel（URLごとにシートを作成した .xlsx）'
                                        }
                                    </div>

                                    {(() => {
                                        const urlsToExport = exportOptions.includeAll
                                            ? urlProcessResults
                                            : urlProcessResults.filter((result: any) => selectedUrls.includes(result.id));

                                        return urlsToExport.length >= 1000 && (
                                            <div className="export-warning">
                                                <AlertTriangle size={16} />
                                                <span>
                                                    大量のURL（{urlsToExport.length}件）をエクスポートする場合、処理に時間がかかる場合があります。
                                                </span>
                                            </div>
                                        );
                                    })()}

                                    {urlProcessResults.length > 100 && (
                                        <div className="export-info">
                                            <strong>最適化情報:</strong> {urlProcessResults.length}件のURLは、ストリーミング処理により効率的にエクスポートされます。
                                        </div>
                                    )}
                                </div>

                                {isExporting && (
                                    <div className="export-progress">
                                        <div className="progress-bar lg no-margin">
                                            <div
                                                className="progress-fill"
                                                style={{ width: `${exportProgress}%` }}
                                            ></div>
                                        </div>
                                        <div className="progress-text">
                                            ファイル生成中... {Math.round(exportProgress)}% ({exportProgressDetails.completed}/{exportProgressDetails.total})
                                        </div>
                                        {urlProcessResults.length > 100 && (
                                            <div className="progress-details">
                                                <small>
                                                    {urlProcessResults.length}件のURLを処理中 - ストリーミング処理により最適化されています
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleExportCancel}
                                    disabled={isExporting}
                                >
                                    キャンセル
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleExportConfirm}
                                    disabled={isExporting}
                                >
                                    {isExporting ? '生成中...' : 'ファイル生成'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default ResultsPage;
